import { useDebounce } from '@chiroup/hooks/useDebounce';
import { use, useEffect, useState } from 'react';
import { QueryFunctionContext, useInfiniteQuery } from 'react-query';
import { LIST_LIMIT } from '../../../constants';
import { MeContext } from '../../../contexts/me.context';
import itemService from '../../../services/item.service';
import { CustomizationListItem } from '../CustomizationListItem.type';

export type QueryKeyNames =
  | 'advice'
  | 'conditions'
  | 'evaluations'
  | 'exercises'
  | 'treatments'
  | 'common-treatments'
  | 'modality'
  | 'supplements';

export const endpoints = {
  advice: 'advice',
  conditions: 'condition',
  evaluations: 'evaluation',
  exercises: 'exercise',
  treatments: 'treatment',
  'common-treatments': 'treatment',
  modality: 'modality',
  supplements: 'supplement',
};

const listQuery = (
  type: string,
  clinicId = -1,
  userId = '',
  limit = LIST_LIMIT,
  extraCols = '',
  showAll = false,
  fullDescr?: boolean
) => {
  return async (context: QueryFunctionContext) => {
    const queryParams = (context.queryKey[1] as { [key: string]: any }) || {};
    const params: any = {
      skip: context.pageParam?.skip || 0,
      limit,
      hide: !showAll,
    };

    if (fullDescr) {
      params.fullDescr = true;
    }

    if (queryParams?.name) {
      params.name = queryParams.name;
    }

    if (queryParams?.descr) {
      params.descr = queryParams.descr;
    }

    if (queryParams?.regions && !Array.isArray(queryParams.regions)) {
      params.region = queryParams?.regions;
    }

    if (queryParams?.category) {
      params.category = queryParams?.category;
    }

    if (queryParams?.type) {
      params.type = queryParams?.type;
    }

    if (extraCols) {
      params.extraCols = extraCols;
    }

    return itemService.list(type, params, clinicId, userId);
  };
};

/**
 *
 * @deprecated not in use anywhere
 */
const useItemList = (
  queryKeyName: QueryKeyNames,
  limit = LIST_LIMIT,
  defaultParams: { [key: string]: string | string[] },
  extraCols?: string,
  showAll?: boolean,
  fullDescr?: boolean
) => {
  const meContext = use(MeContext);
  const [searchParams, setSearchParams] = useState(defaultParams);
  const debouncedParams = useDebounce(searchParams);
  const [queryKey, setQueryKey] = useState<[string, any]>([queryKeyName, defaultParams]);
  const {
    status,
    data,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetched,
    isSuccess,
  } = useInfiniteQuery<{ data: CustomizationListItem[]; skip: number }>(
    queryKey,
    listQuery(
      endpoints[queryKeyName],
      meContext?.me.selectedClinic?.ID,
      meContext?.me.ID,
      limit,
      extraCols,
      showAll,
      fullDescr
    ),
    {
      getNextPageParam: lastGroup => {
        return lastGroup.data?.length === limit ? { skip: lastGroup?.skip } : undefined;
      },
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    const params = queryKey[1];
    let paramsToUse = {
      ...JSON.parse(JSON.stringify(params)),
      ...debouncedParams,
    };

    if (debouncedParams.regions || params.regions) {
      const newRegions = debouncedParams?.regions ? debouncedParams.regions.join(',') : '';
      paramsToUse = {
        ...debouncedParams,
        regions: newRegions,
      };
    }

    const searchChanged = Object.keys(paramsToUse).some(
      param =>
        paramsToUse[param] !==
        (Array.isArray(params[param]) ? params[param].join(',') : params[param])
    );

    if (searchChanged) {
      setQueryKey([queryKeyName, paramsToUse]);
    }
  }, [debouncedParams, queryKeyName, queryKey]);

  const onSearch = (params: Partial<any>) => {
    setSearchParams(params);
  };

  return {
    status,
    data,
    error,
    isFetching,
    isFetched,
    isSuccess,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    onSearch,
    setQueryKey,
  };
};
